import React, {
    createContext,
    useCallback,
    useRef,
    useMemo,
    useState,
    PropsWithChildren,
} from 'react';
import { ToastPayload } from './types';
import { ToastContainer } from './ToastContainer';

type ToastContextType = {
    showToast: (
        message: string,
        options?: {
            duration?: number;
            onPress?: () => void;
            type?: string;
        }
    ) => void;
};

export const ToastContext = createContext<ToastContextType>({
    showToast: () => { },
});

export const ToastProvider: React.FC<PropsWithChildren> = ({ children }) => {
    const [toasts, setToasts] = useState<ToastPayload[]>([]);
    const timers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

    const removeToast = useCallback((id: string) => {
        setToasts(prev => prev.filter(t => t.id !== id));

        if (timers.current[id]) {
            clearTimeout(timers.current[id]);
            delete timers.current[id];
        }
    }, []);

    const showToast = useCallback(
        (message: string, options?: { duration?: number; onPress?: () => void; type?: string }) => {
            const id = `${Date.now()}-${Math.random()}`;
            const duration = options?.duration ?? 15000;

            const handlePress = () => {
                removeToast(id);        //  remove immediately
                options?.onPress?.();   // then do navigation / action
            };

            setToasts(prev => [
                {
                    id,
                    message,
                    duration,
                    onPress: options?.onPress ? handlePress : undefined,
                    type: options?.type || 'Notification'
                },
                ...prev,
            ]);

            // 🔥 single source of truth
            timers.current[id] = setTimeout(() => {
                removeToast(id);
            }, duration);
        },
        [removeToast]
    );

    return (
        <ToastContext.Provider value={{ showToast }}>
            {children}
            <ToastContainer toasts={toasts} onRemove={removeToast} />
        </ToastContext.Provider>
    );
};
